import { FadeUp, Eyebrow, LineReveal } from "@/components/Motion";
import SEO from "@/components/SEO";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";

const TEAM = [
  {
    name: "Haad Bakshi",
    role: "CEO & Producer",
    bio: "Founder of Incolour Films — director, producer, and cinematographer across the studio's films, commercials, and music videos.",
    img: "https://cdn.sanity.io/images/ivs65dh9/production/d4a2b1e97328bffbb4b7256cdaa7d7bd4ed0604b-800x800.jpg?w=800&h=800&fit=crop&auto=format",
  },
  {
    name: "Zehra Abrar",
    role: "Executive Producer & Lawyer",
    bio: "Guides productions from financing through contracts — keeping the studio's deals as clean as its frames.",
    img: "/logo-mark.webp",
  },
  {
    name: "Lee Nisar",
    role: "Producer & Director",
    bio: "Produces and directs across the narrative slate, from first treatment to final cut.",
    img: "/logo-mark.webp",
  },
  {
    name: "Moiz Mufti",
    role: "Business Development & Accounts",
    bio: "First call for new productions — and the steady hand on budgets and accounts that keeps every shoot moving.",
    img: "/logo-mark.webp",
  },
  {
    name: "Piera Savage",
    role: "Entertainment Lawyer & Tax Credits",
    bio: "Production legal, chain of title, and the tax-credit strategy that makes shooting in Canada pay.",
    img: "/logo-mark.webp",
  },
  {
    name: "Aivry Napier",
    role: "Production Designer",
    bio: "Builds the worlds — sets, props, and the details that make a frame believable.",
    img: "/logo-mark.webp",
  },
];

const CHAPTERS = [
  {
    n: "01",
    title: "A Place for Different Voices",
    body: "Incolour was built so filmmakers from different cultures, communities, and lived experiences could bring distinct stories into the same room — and have those stories taken seriously.",
    accent: "text-orange",
  },
  {
    n: "02",
    title: "Bring Us the Story",
    body: "We want to meet filmmakers with something specific to say. Whether a project is a first script or an established filmmaker's next film, we look for a clear point of view and the potential to build it collaboratively.",
    accent: "text-blue",
  },
  {
    n: "03",
    title: "Community Into Production",
    body: "Our role is to turn creative ambition into a producible plan — bringing together producers, crews, vendors, legal, accounting, and production infrastructure around the needs of the project.",
    accent: "text-violet",
  },
];

const PARTNERS = [
  "ISNA Canada",
  "Maple Acura",
  "Bellwoods Coffee & Gelato",
  "Inhouse",
  "Harman",
  "Notifi",
  "Promethea",
  "Lie Down Films",
  "SOTO",
];



export default function About() {
  return (
    <div className="pt-16" data-testid="about-page">
      <SEO
        title="About Our Toronto Film Production Company"
        description="Incolour Films is a Toronto production company bringing filmmakers from diverse backgrounds together to develop original films and providing full-service production across Ontario."
        path="/about"
      />

      <section className="mx-auto max-w-7xl px-4 pb-16 pt-16 sm:px-6 lg:px-8 lg:pt-24">
        <Eyebrow>About Incolour</Eyebrow>

        <h1
          className="mt-5 font-display text-4xl font-extrabold leading-[1.0] tracking-tight sm:text-6xl"
          data-testid="about-title"
        >
          <LineReveal
            lines={[
              "A Toronto production company",
              "built like a community.",
            ]}
          />
        </h1>

        <FadeUp delay={0.3}>
          <p className="mt-8 max-w-3xl text-base leading-relaxed text-muted-foreground sm:text-lg">
            Incolour Films was founded in 2019 as a home for filmmakers with
            distinct points of view. We develop original films, bring people
            from different cultures and creative backgrounds together around
            the work, and provide full-service production for projects shooting
            across Toronto and Ontario.
          </p>
        </FadeUp>
      </section>

      {/* STORY */}
      <section className="mx-auto grid max-w-7xl gap-10 px-4 py-16 sm:px-6 lg:grid-cols-2 lg:px-8 lg:py-24">
        <FadeUp>
          <div className="overflow-hidden rounded-2xl border border-border">
            <img
              src="https://images.unsplash.com/photo-1517935706615-2717063c2225?auto=format&fit=crop&w=1600&q=80"
              alt="Toronto skyline at dusk"
              loading="lazy"
              className="aspect-[4/3] w-full object-cover"
            />
          </div>
        </FadeUp>

        <FadeUp delay={0.12} className="flex flex-col justify-center">
          <Eyebrow>The Story</Eyebrow>

          <h2 className="mt-4 font-display text-3xl font-bold tracking-tight sm:text-4xl">
            From a one-man camera kit to a full-service studio.
          </h2>

          <p className="mt-6 text-base leading-relaxed text-muted-foreground">
            Incolour began with founder Haad Bakshi working across commercials,
            music videos, shorts, and independent film in the GTA. Over time,
            those projects built a wider circle of filmmakers, producers,
            technicians, and creative collaborators — and the company grew
            around that community.
          </p>

          <p className="mt-4 text-base leading-relaxed text-muted-foreground">
            What has stayed constant is the belief behind the name: different
            perspectives make the work stronger. That idea now carries through
            development, producing, service production, and the way we build
            teams around each project.
          </p>
        </FadeUp>
      </section>

      {/* MANIFESTO */}
      <section
        className="border-y border-border bg-cream2 py-16 dark:bg-card lg:py-24"
        data-testid="about-manifesto"
      >
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Eyebrow>What Think Incolour Means</Eyebrow>

          <div className="mt-5 grid gap-8 lg:grid-cols-[0.9fr_1.1fr] lg:gap-16">
            <h2 className="max-w-xl font-display text-3xl font-bold tracking-tight sm:text-4xl lg:text-5xl">
              Different voices belong in the room — and on the screen.
            </h2>

            <div>
              <p className="text-base leading-relaxed text-muted-foreground sm:text-lg">
                Think Incolour is more than a tagline. It is the idea that
                filmmakers should not have to flatten what makes their stories
                specific in order to get them made. We want Incolour to be a
                place where culture, perspective, and lived experience are
                treated as creative strengths.
              </p>

              <p className="mt-4 text-base leading-relaxed text-muted-foreground sm:text-lg">
                If you have a script, film, or project you believe deserves to
                exist, we want to hear about it.
              </p>

              <Link
                to="/contact"
                className="group mt-6 inline-flex items-center gap-2 text-sm font-semibold"
                data-testid="about-bring-story-link"
              >
                Bring us your story
                <ArrowUpRight
                  size={16}
                  className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                />
              </Link>
            </div>
          </div>

          <div className="mt-12 space-y-0">
            {CHAPTERS.map((c, i) => (
              <FadeUp key={c.n} delay={i * 0.1}>
                <div className="group grid gap-4 border-t border-border py-10 transition-colors last:border-b hover:bg-background lg:grid-cols-12 lg:items-baseline lg:px-6">
                  <p
                    className={`font-mono text-5xl font-medium lg:col-span-2 ${c.accent}`}
                  >
                    {c.n}
                  </p>

                  <h3 className="font-display text-2xl font-bold tracking-tight sm:text-3xl lg:col-span-4">
                    {c.title}
                  </h3>

                  <p className="text-base leading-relaxed text-muted-foreground lg:col-span-6">
                    {c.body}
                  </p>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* TEAM */}
      <section
        className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8 lg:py-24"
        data-testid="team-section"
      >
        <Eyebrow>The People</Eyebrow>

        <h2 className="mt-4 font-display text-3xl font-bold tracking-tight sm:text-4xl">
          Small team, deep bench.
        </h2>

        <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {TEAM.map((t, i) => (
            <FadeUp key={t.name} delay={i * 0.08}>
              <div
                className="group relative overflow-hidden rounded-2xl border border-border"
                data-testid={`team-member-${t.name
                  .toLowerCase()
                  .replace(/\s+/g, "-")}`}
              >
                <img
                  src={t.img}
                  alt={t.name}
                  loading="lazy"
                  className={`aspect-[4/5] w-full transition-transform duration-700 group-hover:scale-[1.04] ${
                    t.img.startsWith("/")
                      ? "bg-cream2 object-contain p-14 dark:bg-card"
                      : "object-cover"
                  }`}
                />

                <div className="absolute inset-0 flex flex-col justify-end bg-gradient-to-t from-ink/90 via-ink/30 to-transparent p-6">
                  <p className="font-display text-xl font-bold text-cream">
                    {t.name}
                  </p>

                  <p className="text-xs font-mono uppercase tracking-[0.2em] text-orange">
                    {t.role}
                  </p>

                  <p className="mt-3 max-h-32 overflow-hidden text-sm leading-relaxed text-cream/80 transition-all duration-500 sm:max-h-0 sm:group-hover:max-h-32">
                    {t.bio}
                  </p>
                </div>
              </div>
            </FadeUp>
          ))}
        </div>
      </section>

      {/* COMMUNITY */}
      <section
        className="bg-ink py-16 text-cream lg:py-24"
        data-testid="community-section"
      >
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <p className="flex items-center gap-2 text-xs font-mono uppercase tracking-[0.22em] text-cream/50">
            <span className="inline-block h-2 w-2 bg-orange" /> Clients &amp; Collaborators
          </p>

          <h2 className="mt-4 max-w-3xl font-display text-3xl font-bold tracking-tight sm:text-4xl">
            Built through relationships, project by project.
          </h2>

          <p className="mt-6 max-w-2xl text-base leading-relaxed text-cream/70">
            Incolour has grown through the filmmakers, clients, organizations,
            and creative partners we have worked with across the GTA. These are
            some of the names that have been part of that journey.
          </p>

          <div className="mt-10 flex flex-wrap gap-3">
            {PARTNERS.map((p) => (
              <span
                key={p}
                className="rounded-full border border-cream/25 px-5 py-2.5 text-sm text-cream/85 transition-colors hover:border-orange hover:text-orange"
              >
                {p}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* PATHWAYS */}
      <section
        className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8 lg:py-24"
        data-testid="about-pathways"
      >
        <Eyebrow>Work With Incolour</Eyebrow>

        <h2 className="mt-4 max-w-2xl font-display text-3xl font-bold tracking-tight sm:text-4xl">
          Two ways into the work.
        </h2>

        <div className="mt-10 grid gap-6 lg:grid-cols-2">
          <FadeUp>
            <Link
              to="/original"
              className="group block h-full rounded-2xl border border-border bg-background p-8 transition-all duration-300 hover:-translate-y-1 hover:border-orange"
              data-testid="about-originals-link"
            >
              <p className="text-xs font-mono uppercase tracking-[0.2em] text-orange">
                Original Work
              </p>

              <h3 className="mt-5 font-display text-2xl font-bold tracking-tight sm:text-3xl">
                Bring us a story.
              </h3>

              <p className="mt-4 max-w-xl text-base leading-relaxed text-muted-foreground">
                We develop and produce original films with filmmakers who have
                a distinct point of view and want a producing partner to help
                move the project forward.
              </p>

              <span className="mt-8 inline-flex items-center gap-2 text-sm font-semibold">
                Explore Original Work
                <ArrowUpRight
                  size={16}
                  className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                />
              </span>
            </Link>
          </FadeUp>

          <FadeUp delay={0.1}>
            <Link
              to="/service"
              className="group block h-full rounded-2xl border border-border bg-background p-8 transition-all duration-300 hover:-translate-y-1 hover:border-blue"
              data-testid="about-service-link"
            >
              <p className="text-xs font-mono uppercase tracking-[0.2em] text-blue">
                Service Production
              </p>

              <h3 className="mt-5 font-display text-2xl font-bold tracking-tight sm:text-3xl">
                Bring your production to Ontario.
              </h3>

              <p className="mt-4 max-w-xl text-base leading-relaxed text-muted-foreground">
                We provide local production support across Toronto and Ontario,
                connecting productions with crews, locations, equipment,
                logistics, and trusted production resources.
              </p>

              <span className="mt-8 inline-flex items-center gap-2 text-sm font-semibold">
                Explore Service Production
                <ArrowUpRight
                  size={16}
                  className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                />
              </span>
            </Link>
          </FadeUp>
        </div>

        <FadeUp delay={0.15} className="mt-10">
          <Link
            to="/contact"
            data-testid="about-cta-link"
            className="inline-flex items-center gap-3 rounded-full bg-cream px-8 py-4 text-sm font-semibold text-ink transition-transform hover:scale-[1.04] hover:bg-orange hover:text-cream"
          >
            Start a conversation <ArrowUpRight size={16} />
          </Link>
        </FadeUp>
      </section>
    </div>
  );
}
